import { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionItem,
  Button,
  DataTable,
  DatePicker,
  DatePickerInput,
  FilterableMultiSelect,
  MultiSelect,
  Pagination,
  Search,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { Add, Download, Filter } from '@carbon/react/icons';
import { EmptyState } from '@carbon/ibm-products';
import { PageHeader } from 'components/page-header';
import { usePricingEnums } from 'pages/pricing/shared/use-pricing-enums';
import type { Contract, Option } from 'pages/pricing/shared/types';
import { parseUTCdateTime } from 'utils/dates';
import styles from './contract-library.module.scss';

const HEADERS = [
  { key: 'name', header: 'Contract' },
  { key: 'commodity', header: 'Commodity' },
  { key: 'legs', header: 'Legs' },
  { key: 'createdBy', header: 'Created by' },
  { key: 'updatedBy', header: 'Updated by' },
  { key: 'createdAt', header: 'Created at' },
  { key: 'updatedAt', header: 'Updated at' },
];

const labelOf = (opts: Option[], value: string | null) =>
  opts.find((o) => o.value === value)?.label ?? value ?? '';

// Backend timestamps are UTC; format them in UTC so the date can't drift a day near midnight.
const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return '';
  return parseUTCdateTime(iso);
};

const inRange = (
  iso: string | null | undefined,
  from: Date | null,
  to: Date | null
) => {
  if (!from && !to) return true;
  if (!iso) return true;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return true;
  if (from && t < from.getTime()) return false;
  if (to) {
    const end = new Date(to);
    end.setHours(23, 59, 59, 999);
    if (t > end.getTime()) return false;
  }
  return true;
};

interface Props {
  contracts: Contract[];
  onOpen: (id: number) => void;
  onAdd: () => void;
}

export function ContractLibrary({ contracts, onOpen, onAdd }: Props) {
  const { enums } = usePricingEnums();
  const commodityOpts = useMemo(() => enums?.commodities ?? [], [enums]);

  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const [commodities, setCommodities] = useState<string[]>([]);
  const [legCounts, setLegCounts] = useState<string[]>([]);
  const [users, setUsers] = useState<string[]>([]);
  const [createdFrom, setCreatedFrom] = useState<Date | null>(null);
  const [createdTo, setCreatedTo] = useState<Date | null>(null);
  const [updatedFrom, setUpdatedFrom] = useState<Date | null>(null);
  const [updatedTo, setUpdatedTo] = useState<Date | null>(null);

  const allUsers = useMemo(
    () =>
      Array.from(
        new Set(
          contracts.flatMap((c) => [c.createdBy, c.updatedBy]).filter(Boolean)
        )
      ).sort(),
    [contracts]
  ) as string[];
  const allLegCounts = useMemo(
    () =>
      Array.from(new Set(contracts.map((c) => String(c.legs.length)))).sort(
        (a, b) => Number(a) - Number(b)
      ),
    [contracts]
  );

  const createdActive = !!(createdFrom || createdTo);
  const updatedActive = !!(updatedFrom || updatedTo);
  const filterCount = [
    commodities.length > 0,
    legCounts.length > 0,
    users.length > 0,
    createdActive,
    updatedActive,
  ].filter(Boolean).length;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return contracts.filter((c) => {
      const commodity = labelOf(commodityOpts, c.commodity);
      if (commodities.length > 0 && !commodities.includes(commodity))
        return false;
      if (legCounts.length > 0 && !legCounts.includes(String(c.legs.length)))
        return false;
      if (
        users.length > 0 &&
        !users.includes(c.createdBy ?? '') &&
        !users.includes(c.updatedBy ?? '')
      )
        return false;
      if (!inRange(c.createdAt, createdFrom, createdTo)) return false;
      if (!inRange(c.updatedAt, updatedFrom, updatedTo)) return false;
      if (!q) return true;
      return (
        c.name.toLowerCase().includes(q) || commodity.toLowerCase().includes(q)
      );
    });
  }, [
    contracts,
    commodityOpts,
    search,
    commodities,
    legCounts,
    users,
    createdFrom,
    createdTo,
    updatedFrom,
    updatedTo,
  ]);

  const rows = useMemo(
    () =>
      filtered.slice((page - 1) * pageSize, page * pageSize).map((c) => ({
        id: String(c.id),
        name: c.name,
        commodity: labelOf(commodityOpts, c.commodity),
        legs: c.legs.length,
        createdBy: c.createdBy ?? '',
        updatedBy: c.updatedBy ?? '',
        createdAt: fmtDate(c.createdAt),
        updatedAt: fmtDate(c.updatedAt),
      })),
    [filtered, page, pageSize, commodityOpts]
  );

  const resetFilters = () => {
    setCommodities([]);
    setLegCounts([]);
    setUsers([]);
    setCreatedFrom(null);
    setCreatedTo(null);
    setUpdatedFrom(null);
    setUpdatedTo(null);
    setSearch('');
    setPage(1);
  };

  const exportCsv = () => {
    const head = HEADERS.map((h) => h.header);
    const body = filtered.map((c) => [
      c.name,
      labelOf(commodityOpts, c.commodity),
      c.legs.length,
      c.createdBy ?? '',
      c.updatedBy ?? '',
      fmtDate(c.createdAt),
      fmtDate(c.updatedAt),
    ]);
    const csv = [head, ...body]
      .map((r) => r.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pricing-contracts.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleLabel =
    filterCount > 0
      ? `${filterCount} filter${filterCount > 1 ? 's' : ''} applied`
      : showFilters
        ? 'Hide filters'
        : 'Show filters';

  return (
    <div className={styles.list}>
      <PageHeader
        breadcrumbs={[]}
        title="Pricing Contracts"
        actions={
          <Button kind="primary" size="lg" renderIcon={Add} onClick={onAdd}>
            Add Pricing Contract
          </Button>
        }
      />

      <section className={styles.main}>
        {showFilters && (
          <aside className={styles.filterPanel}>
            <div className={styles.filterHeader}>
              <Button
                kind="ghost"
                size="sm"
                disabled={filterCount === 0 && search === ''}
                onClick={resetFilters}
              >
                Reset filters
              </Button>
            </div>

            <Accordion>
              <AccordionItem title="Dates" open>
                <div className={styles.dateGroup}>
                  <span className={styles.groupLabel}>Created at</span>
                  <DatePicker
                    datePickerType="range"
                    value={[createdFrom, createdTo].filter(Boolean) as Date[]}
                    onChange={(dates: Date[]) => {
                      setCreatedFrom(dates[0] ?? null);
                      setCreatedTo(dates[1] ?? null);
                      setPage(1);
                    }}
                  >
                    <DatePickerInput
                      id="ct-created-from"
                      placeholder="mm/dd/yyyy"
                      labelText="From"
                      size="sm"
                    />
                    <DatePickerInput
                      id="ct-created-to"
                      placeholder="mm/dd/yyyy"
                      labelText="To"
                      size="sm"
                    />
                  </DatePicker>
                </div>
                <div className={styles.dateGroup}>
                  <span className={styles.groupLabel}>Updated at</span>
                  <DatePicker
                    datePickerType="range"
                    value={[updatedFrom, updatedTo].filter(Boolean) as Date[]}
                    onChange={(dates: Date[]) => {
                      setUpdatedFrom(dates[0] ?? null);
                      setUpdatedTo(dates[1] ?? null);
                      setPage(1);
                    }}
                  >
                    <DatePickerInput
                      id="ct-updated-from"
                      placeholder="mm/dd/yyyy"
                      labelText="From"
                      size="sm"
                    />
                    <DatePickerInput
                      id="ct-updated-to"
                      placeholder="mm/dd/yyyy"
                      labelText="To"
                      size="sm"
                    />
                  </DatePicker>
                </div>
              </AccordionItem>
            </Accordion>

            <Accordion>
              <AccordionItem title="Pricing contract filters" open>
                <div className={styles.filterGroup}>
                  <MultiSelect
                    id="ct-f-commodity"
                    titleText="Commodity"
                    label="Any commodity"
                    size="sm"
                    items={commodityOpts.map((o) => o.label)}
                    initialSelectedItems={commodities}
                    onChange={({ selectedItems }) => {
                      setCommodities((selectedItems as string[]) ?? []);
                      setPage(1);
                    }}
                  />
                </div>
                <div className={styles.filterGroup}>
                  <MultiSelect
                    id="ct-f-legs"
                    titleText="Number of legs"
                    label="Any number of legs"
                    size="sm"
                    items={allLegCounts}
                    initialSelectedItems={legCounts}
                    onChange={({ selectedItems }) => {
                      setLegCounts((selectedItems as string[]) ?? []);
                      setPage(1);
                    }}
                  />
                </div>
                <div className={styles.filterGroup}>
                  <FilterableMultiSelect
                    id="ct-f-users"
                    titleText="Users"
                    placeholder="Search users…"
                    size="sm"
                    items={allUsers}
                    initialSelectedItems={users}
                    onChange={({ selectedItems }) => {
                      setUsers((selectedItems as string[]) ?? []);
                      setPage(1);
                    }}
                  />
                </div>
              </AccordionItem>
            </Accordion>
          </aside>
        )}

        <div className={styles.tableArea}>
          <div className={styles.toolbar}>
            <Button
              kind="ghost"
              renderIcon={Filter}
              onClick={() => setShowFilters((v) => !v)}
            >
              {toggleLabel}
            </Button>
            <Search
              size="lg"
              labelText="Search contracts"
              placeholder="Search contracts…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              onClear={() => setSearch('')}
              className={styles.toolbarSearch}
            />
            <Button
              kind="ghost"
              size="lg"
              hasIconOnly
              renderIcon={Download}
              iconDescription="Export CSV"
              tooltipPosition="bottom"
              tooltipAlignment="end"
              onClick={exportCsv}
            />
          </div>

          <div className={styles.tableContainer}>
            {filtered.length === 0 ? (
              <EmptyState
                size="lg"
                title="No contracts found"
                subtitle={
                  filterCount > 0 || search.length > 0
                    ? 'Try adjusting your filters or search terms.'
                    : 'You don’t have any contracts yet. Add one to get started.'
                }
                action={
                  filterCount > 0 || search.length > 0
                    ? { text: 'Reset filters', onClick: resetFilters }
                    : { text: 'Add Pricing Contract', onClick: onAdd }
                }
              />
            ) : (
              <DataTable rows={rows} headers={HEADERS} isSortable>
                {({
                  rows: tableRows,
                  headers,
                  getTableProps,
                  getHeaderProps,
                  getRowProps,
                }) => (
                  <Table {...getTableProps()} size="lg">
                    <TableHead>
                      <TableRow>
                        {headers.map((h) => {
                          const { key, ...rest } = getHeaderProps({
                            header: h,
                          });
                          return (
                            <TableHeader key={key ?? h.key} {...rest}>
                              {h.header}
                            </TableHeader>
                          );
                        })}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tableRows.map((row) => {
                        const { key, ...rest } = getRowProps({ row });
                        return (
                          <TableRow key={key ?? row.id} {...rest}>
                            {row.cells.map((cell) => {
                              if (cell.info.header === 'name') {
                                return (
                                  <TableCell key={cell.id}>
                                    <button
                                      type="button"
                                      className={styles.nameLink}
                                      onClick={() => onOpen(Number(row.id))}
                                    >
                                      {cell.value}
                                    </button>
                                  </TableCell>
                                );
                              }
                              return (
                                <TableCell key={cell.id}>
                                  {cell.value}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </DataTable>
            )}
          </div>

          <Pagination
            backwardText="Previous page"
            forwardText="Next page"
            itemsPerPageText="Contracts per page:"
            totalItems={filtered.length}
            pageSize={pageSize}
            pageSizes={[25, 50, 75, 100]}
            page={page}
            onChange={({ page: p, pageSize: ps }) => {
              setPage(p);
              setPageSize(ps);
            }}
          />
        </div>
      </section>
    </div>
  );
}
